import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const urlCache = new Map<string, { url: string; expires: number }>();
const EXPIRY = 3500 * 1000; // slightly less than 1h

export const getSignedImageUrl = async (
  storagePath: string,
  bucket = 'instrument-images'
): Promise<string> => {
  const key = `${bucket}/${storagePath}`;
  const cached = urlCache.get(key);
  if (cached && cached.expires > Date.now()) return cached.url;

  const { data } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, 3600);

  if (data?.signedUrl) {
    urlCache.set(key, { url: data.signedUrl, expires: Date.now() + EXPIRY });
    return data.signedUrl;
  }
  return '';
};

export const getSignedImageUrls = async (
  storagePaths: string[],
  bucket = 'instrument-images'
): Promise<Map<string, string>> => {
  const result = new Map<string, string>();
  const uncached: string[] = [];

  for (const path of storagePaths) {
    const key = `${bucket}/${path}`;
    const cached = urlCache.get(key);
    if (cached && cached.expires > Date.now()) {
      result.set(path, cached.url);
    } else {
      uncached.push(path);
    }
  }

  if (uncached.length > 0) {
    const { data } = await supabase.storage
      .from(bucket)
      .createSignedUrls(uncached, 3600);

    if (data) {
      for (const item of data) {
        if (item.signedUrl && item.path) {
          const key = `${bucket}/${item.path}`;
          urlCache.set(key, { url: item.signedUrl, expires: Date.now() + EXPIRY });
          result.set(item.path, item.signedUrl);
        }
      }
    }
  }

  return result;
};

/** Hook: returns a map of storage_path → signed URL for an array of paths */
export const useSignedUrls = (
  storagePaths: string[],
  bucket = 'instrument-images'
) => {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (storagePaths.length === 0) {
      setUrls(new Map());
      return;
    }
    let cancelled = false;
    getSignedImageUrls(storagePaths, bucket).then(map => {
      if (!cancelled) setUrls(map);
    });
    return () => { cancelled = true; };
  }, [storagePaths.join(','), bucket]);

  return urls;
};
